import { STORAGE_KEYS } from '@/constants/storageKeys'
import { apiClient } from './api'
import { getEnvConfig } from './runtime-env'
import type { VideoCall } from '@/types'

export interface ModerationFrameUploadPayload {
  call_id?: string
  frame_seq: number
  captured_at: string
  image_base64: string
  width: number
  height: number
}

export interface ModerationFrameUploadResponse {
  id: string
  status: 'pending' | 'checked' | 'failed'
  decision?: 'pass' | 'review' | 'violation' | ''
}

export interface AnchorActivityEventPayload {
  event_type: string
  event_source?: 'anchor_h5_websocket' | 'anchor_h5_rtc' | 'anchor_h5_ui'
  order_id?: string
  call_direction?: 'user_to_anchor' | 'anchor_to_user'
  counterparty_user_id?: string
  reason_code?: string
  result?: 'success' | 'failed' | 'ignored'
  from_call_phase?: string
  to_call_phase?: string
  frontend_session_id?: string
  rtc_channel?: string
  rtc_uid?: number
  error_code?: string
  error_message?: string
  metadata?: Record<string, unknown>
}

export const callApi = {
  acceptOrder(
    orderId: string,
    payload?: { channel_name?: string; agora_uid?: number },
  ) {
    return apiClient.post<VideoCall>(`/orders/${orderId}/accept`, payload)
  },
  connectedCall(orderId: string) {
    return apiClient.post<VideoCall>(`/calls/${orderId}/connected`)
  },
  rejectOrder(orderId: string, payload?: { reason_code?: string }) {
    return apiClient.post<VideoCall>(`/orders/${orderId}/reject`, payload)
  },
  recordIncomingOrderReceived(orderId: string) {
    return apiClient.post<{ order_id: string; recorded: boolean }>(
      `/orders/${orderId}/incoming-received`,
    )
  },
  endCall(callId: string) {
    return apiClient.post<VideoCall>(`/calls/${callId}/end`)
  },
  releaseCall(
    orderId: string,
    payload: {
      event_type: 'call_rejected' | 'call_timeout' | 'call_canceled'
      reason_code?: string
    },
  ) {
    return apiClient.post<VideoCall>(`/calls/${orderId}/release`, payload)
  },
  releaseOrder(
    orderId: string,
    payload: {
      event_type:
        | 'call_ended'
        | 'call_rejected'
        | 'call_timeout'
        | 'call_canceled'
      reason_code?: string
    },
  ) {
    return apiClient.post<{
      order_id: string
      released: boolean
      ignored?: boolean
      reason?: string
    }>(`/orders/${orderId}/release`, payload)
  },
  recordOrderTimeoutMissed(orderId: string) {
    return apiClient.post<{ order_id: string; recorded: boolean }>(
      `/orders/${orderId}/timeout-missed`,
    )
  },
  uploadModerationFrame(
    orderId: string,
    payload: ModerationFrameUploadPayload,
  ) {
    return apiClient.post<ModerationFrameUploadResponse>(
      `/orders/${orderId}/moderation-frames`,
      payload,
    )
  },
  reportActivityEvent(payload: AnchorActivityEventPayload) {
    return silentActivityEventRequest(payload)
  },
  renewCallToken(orderId: string, payload?: { channel_name?: string }) {
    return apiClient.post<VideoCall>(`/calls/${orderId}/renew-token`, payload)
  },
  getCallHistory(params?: { page?: number; page_size?: number }) {
    return apiClient.getPaginated<VideoCall>('/video-call/history', params)
  },
}

async function silentActivityEventRequest(payload: AnchorActivityEventPayload) {
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem(STORAGE_KEYS.accessToken)
      : apiClient.getToken()
  if (!token) {
    return { code: 0, message: '', data: { recorded: false } }
  }
  const response = await fetch(
    `${getEnvConfig().API_BASE_URL}/activity-events`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    },
  )
  if (!response.ok) {
    return { code: response.status, message: '', data: { recorded: false } }
  }
  try {
    return await response.json()
  } catch {
    return { code: 0, message: '', data: { recorded: true } }
  }
}
